import * as React from 'react';
import { useQuery } from 'react-apollo';
import gql from 'graphql-tag';
import { GetGroupByPlurals, GetGroupBy, formatNumber, getSuggestedMaxTick, getMaxTickLimit, getLabels } from '../../helpers/ChartHelper';
import { SearchCriteria } from '../SearchCriteria';
import CloseIcon from '@material-ui/icons/Close';
import { ChartCommonProps, IconButtonWrapper } from '../Common';
import { makeStyles, Theme, createStyles, IconButton } from '@material-ui/core';
import { ChartFilter } from '../ChartComponents';
import { SelectedItemProps } from '../MattersByStatusChart';
import { DrillDownType } from '../DrillDownType';
import { Bar, getElementAtEvent } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, Scale, Tick, Chart, ChartEvent, ActiveElement } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import AspectRatioIcon from '@material-ui/icons/AspectRatio';
import { useScreenshot, createFileName } from 'use-react-screenshot';
import MTooltip from '@material-ui/core/Tooltip';
import { ChartInfo } from '../../components/ChartInfo';

import { RV_CHART_CLASS, RV_CHART_COLORS, rvBarDatasetStyle, rvChartAnimation, rvValueAxisStyle, rvCategoryAxisStyle, rvTooltipStyle, isChartDataEmpty } from '../chartTheme';
import { ChartEmptyState } from '../ChartEmptyState';
import { ChartHeading } from '../ChartHeading';
import { ChartLimitNote } from '../ChartLimitNote';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import { AnimatedNumber } from '../AnimatedNumber';
import MoneyOffOutlinedIcon from '@material-ui/icons/MoneyOffOutlined';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, zoomPlugin);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',
            paddingBottom: '10px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            // minHeight: '100%',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            [`& ${`span`}`]: {
                // marginBottom: '15px',
                // display: 'block',
                // textAlign: 'center',
                // with: '100%',
            },
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
            },
        },
        barContainer: {
            padding: '5px 15px 15px 15px',
            minHeight: '300px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: '350px',
            maxWidth: 'auto',
            position: 'relative',
            '@media (max-width: 533px)': {
                maxWidth: '350px',
                margin: '0 auto',
            },
            [`& .${`titleWrapper`}`]: {
                textAlign: 'center',
                display: 'inline-block',
                width: '100%',
                padding: '15px',
                [`& .${`title`}`]: {
                    fontSize: '18px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                [`& .${`value`}`]: {
                    fontSize: '24px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                [`& .${`secondary-title`}`]: {
                    fontSize: '16px',
                    fontWeight: 400,
                    display: 'block',
                    marginBottom: '15px',
                },
            },
            '&:hover': {
                '& .scroll': {
                    display: 'flex !important',
                    '& .Mui-disabled': {
                        '& .MuiSvgIcon-root': {
                            color: 'inherit !important',
                        },
                    },
                },
            },
        },
        barTitleContainer: {
            display: 'flex',
            flexFlow: 'column',
            alignItems: 'center',
            padding: '15px 15px 0px 15px',
            fontSize: '16px',
            fontWeight: 600,
            [`& .${`h1`}`]: {},
            [`& .${`h2`}`]: {
                fontSize: '2.5rem',
                padding: '10px 0',
            },
            [`& .${`h3`}`]: {
                paddingTop: '10px',
            },
        },
        totalDiv: {
            cursor: 'pointer',
            paddingRight: 5,
        },
        loadingWrapper: {
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            position: 'relative',
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            '@media print': {
                display: 'none !important',
            },
        },
        footerDescription: {
            fontSize: '1rem',
            textAlign: 'center',
        },
        creditCount: {
            padding: '10px 0px',
            fontWeight: 600,
            textAlign: 'center',
            fontSize: '18px'
        },
        scrollUp: {
            position: 'absolute',
            top: '30px',
            justifyContent: 'center',
            transform: 'translateX(-50%)',
            left: '50%',
            minWidth: '54px',
        },
        scrollDown: {
            position: 'absolute',
            bottom: '90px',
            justifyContent: 'center',
            transform: 'translateX(-50%)',
            left: '50%',
            minWidth: '54px',
        },
    }),
);
interface ChartProps extends SelectedItemProps, ChartCommonProps {
    groupBy: string;
    chartFilter?: ChartFilter;
}

interface ChartState {
    total: number;
    groupedByCount: number;
    amounts: number[];
    groupedBy: string[];
    matters: number[];
    symbol: string;
    creditCount?: number;
}

export const DebtOwingChart: React.FC<ChartProps> = (props) => {
    const classes = useStyles();

    // tslint:disable-next-line:no-any
    const chartRef = React.useRef<any>();

    // tslint:disable-next-line:no-any
    const entireChartRef = React.useRef<any>();

    // tslint:disable-next-line:no-any
    const scrollUpAndPrevRef = React.useRef<any>();

    // tslint:disable-next-line:no-any
    const scrollDownAndNextRef = React.useRef<any>();

    const [hideControl, setHideControl] = React.useState<boolean>(false);

    const takeScreenShot = useScreenshot({
        type: 'image/jpeg',
        quality: 1.0,
    })[1];

    const [state, setState] = React.useState<ChartState>({
        total: 0,
        groupedByCount: 0,
        amounts: [],
        groupedBy: [],
        matters: [],
        symbol: '$',
    });

    React.useEffect(() => {
        window.addEventListener('keydown', onKeyDownHandler);

        return () => {
            window.removeEventListener('keydown', onKeyDownHandler);
        };
        // tslint:disable-next-line: align
    }, []);

    // tslint:disable-next-line: no-any
    const onKeyDownHandler = (event: any) => {
        if (event.keyCode === 80 && (event.ctrlKey || event.metaKey) && !event.altKey) {
            event.preventDefault();
            if (event.stopImmediatePropagation) {
                event.stopImmediatePropagation();
            } else {
                event.stopPropagation();
            }
            return;
        }
    };

    const currencySymbolQuery = useQuery<CurrencySymbolData>(CURRENCY_SYMBOL_QUERY);

    React.useEffect(() => {
        if (!currencySymbolQuery.loading && !currencySymbolQuery.error && currencySymbolQuery.data) {
            setState((prevState) => {
                const symbol = currencySymbolQuery.data!.options.currencySymbol.symbol;

                return {
                    ...prevState,
                    symbol: symbol,
                };
            });
        }
        // tslint:disable-next-line: align
    }, [currencySymbolQuery.loading, currencySymbolQuery.data, currencySymbolQuery.error]);

    const unbilledFeesQuery = useQuery<DebtOwingData, DebtOwingParams>(DEBT_OWING_QUERY, {
        variables: {
            entityGuid: props.chartFilter && props.chartFilter.entityGuid !== undefined ? props.chartFilter.entityGuid : null,
            showAllRecords: props.showAllRecords,
            entityType: props.chartFilter && props.chartFilter.entityType !== undefined ? props.chartFilter.entityType : null,
            groupBy: props.groupBy,
            responsibility: props.chartFilter && props.chartFilter.responsibility !== undefined ? props.chartFilter.responsibility : null,
            include: null,
        },
    });

    React.useEffect(() => {
        if (
            !unbilledFeesQuery.loading &&
            !unbilledFeesQuery.error &&
            unbilledFeesQuery.data &&
            unbilledFeesQuery.data.statistics &&
            unbilledFeesQuery.data.statistics.matter &&
            unbilledFeesQuery.data.statistics.matter.debtsOwing
        ) {
            setState((prevState) => {
                const { debtOwing, groupedByCount, total, creditCount } = unbilledFeesQuery.data!.statistics.matter.debtsOwing;

                const amounts = debtOwing.map((debt: DebtOwing) => {
                    return debt.amount;
                });

                const matters = debtOwing.map((debt: DebtOwing) => {
                    return debt.matters;
                });

                const groupedBy = debtOwing.map((debt: DebtOwing) => {
                    return debt.groupedBy;
                });

                return {
                    ...prevState,
                    groupedByCount,
                    total,
                    amounts,
                    groupedBy,
                    matters,
                    creditCount
                };
            });
        }
        // tslint:disable-next-line: align
    }, [unbilledFeesQuery.loading, unbilledFeesQuery.error, unbilledFeesQuery.data]);

    const handleTotalClick = () => {
        if (props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();

            searchCriterias.push({ property: 'DebtOwing', operator: 'gt', comparison: '0' });

            props.getSelectedItem(searchCriterias, true, `Total Debt Owing by ${GetGroupBy(props.groupBy)}`, DrillDownType.matterSummaryDrilldown, 'Debtors');
        }
    };

    const onCloseChart = () => {
        if (props.onCloseChart) {
            return props.onCloseChart(`isDebtOwingBy${props.groupBy}ChartOpen`);
        }
    };

    const onScrollUpAndPrev = (type: 'up' | 'prev') => {
        if (chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min > 0) {
            const scrollRatio = chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].max - chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min;
            const min = chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min;
            if (scrollRatio < chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min) {
                chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min -= scrollRatio ? scrollRatio : 1;
                chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].max -= scrollRatio ? scrollRatio : 1;
            } else {
                chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].min -= min;
                chartRef.current.options.scales[type === 'up' ? 'y' : 'x'].max -= min;
            }
            chartRef.current.options.scales[type === 'up' ? 'x' : 'y'].min = 0;
            chartRef.current.update();
            // enabled scroll up
            scrollUpAndPrevRef.current.classList.remove('Mui-disabled');
        } else {
            // disabled scroll up
            scrollUpAndPrevRef.current.classList.add('Mui-disabled');
        }
        scrollDownAndNextRef.current.classList.remove('Mui-disabled');
    };

    const onScrollDownAndNext = (type: 'down' | 'next') => {
        const labels = state.groupedBy;

        if (chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max < labels.length) {
            const remainingBar = labels.length - chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max;
            const scrollRatio = chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max - chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].min;
            if (remainingBar <= scrollRatio) {
                chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].min += remainingBar;
                chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max += remainingBar;
            } else {
                chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].min += scrollRatio ? scrollRatio : 1;
                chartRef.current.options.scales[type === 'down' ? 'y' : 'x'].max += scrollRatio ? scrollRatio : 1;
            }
            chartRef.current.options.scales[type === 'down' ? 'x' : 'y'].min = 0;
            chartRef.current.update();
            // enabled scroll down
            scrollDownAndNextRef.current.classList.remove('Mui-disabled');
        } else {
            // disabled scroll down
            scrollDownAndNextRef.current.classList.add('Mui-disabled');
        }
        scrollUpAndPrevRef.current.classList.remove('Mui-disabled');
    };

    const onScollBar = (type: 'up' | 'down' | 'prev' | 'next') => () => {
        if (type === 'up') {
            onScrollUpAndPrev('up');
        } else if (type === 'down') {
            onScrollDownAndNext('down');
        } else if (type === 'next') {
            onScrollDownAndNext('next');
        } else {
            onScrollUpAndPrev('prev');
        }
    };

    const onDisabledEnabledArrow = (barType: 'horizontal' | 'vertical') => {
        const labels = state.groupedBy;
        // up
        if (chartRef.current.options.scales[barType === 'horizontal' ? 'y' : 'x'].min > 0) {
            scrollUpAndPrevRef.current.classList.remove('Mui-disabled');
        } else {
            scrollUpAndPrevRef.current.classList.add('Mui-disabled');
        }

        // down
        if (chartRef.current.options.scales[barType === 'horizontal' ? 'y' : 'x'].max < labels.length) {
            scrollDownAndNextRef.current.classList.remove('Mui-disabled');
        } else {
            scrollDownAndNextRef.current.classList.add('Mui-disabled');
        }
    };

    const onPan = (context: { chart: Chart }) => {
        onDisabledEnabledArrow('horizontal');

        chartRef.current.options.scales.x.min = 0;
        chartRef.current.update();
    };

    const onHover = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
        (event?.native?.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'auto';
    };

    const onZoom = (context: { chart: Chart }) => {
        onDisabledEnabledArrow('horizontal');

        chartRef.current.options.scales.x.min = 0;
        chartRef.current.update();
    };

    const onMaximize = () => {
        if (props.onMaximize) {
            props.onMaximize();
        }
    };

    // tslint:disable-next-line: no-any
    const onContextMenu = (event: any) => {
        event.preventDefault();
        if (event.button !== 2) {
            return false;
        }
        return true;
    };

    const onDownload =
        (name: string, extension: string = 'jpg') =>
        // tslint:disable-next-line: no-any
        (image: any) => {
            const a = document.createElement('a');
            a.href = image;
            a.download = createFileName(extension, name);
            a.click();
            setHideControl(false);
        };

    // tslint:disable-next-line: no-any
    const onDownloadScreenshot = (event: any) => {
        setHideControl(true);
        event.preventDefault();
        event.stopPropagation();
        setTimeout(() => {
            takeScreenShot(entireChartRef.current).then(onDownload(`Total Debt Owing by ${GetGroupBy(props.groupBy)}`));
            // tslint:disable-next-line: align
        }, 1000);
    };

    const chartControlComponent = () => {
        return (
            <>
<IconButtonWrapper aria-label="close">
                    <ChartInfo 
                        description={props.description}
                        fontSize={props.showAllRecords ? 'large' : 'medium'}
                        className={`${classes.helpIcon} rv-chart-help`}
                    />
                    <IconButton size={props.showAllRecords ? 'medium' : 'small'} onClick={onDownloadScreenshot}>
                        <GetAppOutlinedIcon color="primary" fontSize={props.showAllRecords ? 'large' : 'medium'} />
                    </IconButton>
                    {!props.showAllRecords && props.onMaximize && (
                        <MTooltip title="Expand chart">
                            <IconButton aria-label="maximize" onClick={onMaximize} size="small">
                                <AspectRatioIcon />
                            </IconButton>
                        </MTooltip>
                    )}
                    <IconButton aria-label="close" onClick={onCloseChart} size={props.showAllRecords ? 'medium' : 'small'}>
                        <CloseIcon fontSize={props.showAllRecords ? 'large' : 'medium'} />
                    </IconButton>
                </IconButtonWrapper>
            </>
        );
    };

    if (unbilledFeesQuery.loading) {
        return (
            <div className="rv-chart rv-chart-loading">
                {chartControlComponent()}
                <ChartSkeleton variant="bars" />
            </div>
        );
    }

    return (
        <div className={`${classes.root} ${RV_CHART_CLASS}${isChartDataEmpty(state.amounts) ? ' rv-chart--empty' : ''}`} ref={entireChartRef}>
            {hideControl ? null : chartControlComponent()}
            <div className={classes.barTitleContainer}>
                <ChartHeading icon={<MoneyOffOutlinedIcon />} title="Total Debt Owing" />
                <div className={`h2 ${classes.totalDiv}`} onClick={() => handleTotalClick()}>
                    <AnimatedNumber value={state.total} format={(amount) => `${state.symbol}${formatNumber(amount)}`} />
                </div>
                <div className="h3 rv-chart-section">Total Debt Owing by {GetGroupBy(props.groupBy)}</div>
            </div>
            <div className={classes.barContainer} style={{ maxHeight: props.showAllRecords ? '100%' : '350px' }}>
                {props.showAllRecords && (
                    <>
                        <div className={`${classes.scrollUp} scroll`}>
                            <IconButton size="medium" onClick={onScollBar('up')} ref={scrollUpAndPrevRef}>
                                <KeyboardArrowUpIcon color="primary" fontSize="large" />
                            </IconButton>
                        </div>
                        <div className={`${classes.scrollDown} scroll`}>
                            <IconButton size="medium" onClick={onScollBar('down')} ref={scrollDownAndNextRef}>
                                <KeyboardArrowDownIcon color="primary" fontSize="large" />
                            </IconButton>
                        </div>
                    </>
                )}
                <Bar
                    ref={chartRef}
                    onContextMenu={onContextMenu}
                    // tslint:disable-next-line: no-any
                    onClick={(event: React.MouseEvent<any>) => {
                        const _data = getElementAtEvent(chartRef.current, event);
                        if (_data.length > 0 && props.getSelectedItem) {
                            const _index = getElementAtEvent(chartRef.current, event)[0].index;

                            const item = state.groupedBy[_index];

                            const searchCriterias = new Array<SearchCriteria>();

                            searchCriterias.push({ property: props.groupBy, operator: 'eq', comparison: item }, { property: 'DebtOwing', operator: 'not', comparison: '0' });

                            props.getSelectedItem(searchCriterias, true, `Total Debt Owing by ${GetGroupBy(props.groupBy)}`, DrillDownType.matterSummaryDrilldown, 'Debtors');
                        }
                    }}
                    data={{
                        labels: getLabels(state.groupedBy),
                        datasets: [
                            {
                                label: 'Debt Owing',
                                ...rvBarDatasetStyle(RV_CHART_COLORS.primary),
                                data: state.amounts,
                            },
                        ],
                    }}
                    options={{
                        onHover: onHover,
                        indexAxis: 'y' as const,
                        animation: rvChartAnimation(),
                        elements: { bar: { borderWidth: 0 } },
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                            x: {
                                grid: rvValueAxisStyle.grid,
                                // beginAtZero: true,
                                suggestedMax: getSuggestedMaxTick(state.amounts),
                                beginAtZero: true,
                                ticks: {
                                    ...rvValueAxisStyle.ticks,
                                    maxTicksLimit: getMaxTickLimit(state.amounts),
                                    autoSkip: false,
                                    callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                        return `${state.symbol}${formatNumber(tickValue)}`;
                                    },
                                },
                            },
                            y: {
                                grid: rvCategoryAxisStyle.grid,
                                min: 0,
                                max: props.showAllRecords ? 29 : 9,
                                ticks: {
                                    ...rvCategoryAxisStyle.ticks,
                                    autoSkip: false,
                                },
                            },
                        },
                        plugins: {
                            legend: {
                                position: 'bottom' as const,
                                display: false,
                            },
                            title: {
                                display: false,
                            },
                            zoom: {
                                pan: {
                                    enabled: props.showAllRecords,
                                    onPan: onPan,
                                },
                                zoom: {
                                    wheel: {
                                        enabled: props.showAllRecords,
                                    },
                                    onZoom: onZoom,
                                },
                                // limits: {
                                //   y: { min: 0, max: 9 },
                                // },
                            },
                            tooltip: {
                                ...rvTooltipStyle,
                                callbacks: {
                                    // tslint:disable-next-line: no-any
                                    label: function (context: any) {
                                        const {
                                            dataIndex,
                                            //   label,
                                            //   formattedValue,
                                            //   dataset
                                        } = context;

                                        var currentValue = state.amounts[dataIndex];

                                        var matterCount = state.matters[dataIndex];

                                        return `Debt Owing: ${state.symbol}${formatNumber(currentValue)}, Matters:${matterCount}`;
                                    },
                                },
                            },
                        },
                    }}
                />
                {isChartDataEmpty(state.amounts) && (
                    <ChartEmptyState title="No debt owing" hint={"Nothing is owing right now."} />
                )}
            </div>
            {/* {state.creditCount && (
                <span className={classes.creditCount}>
                   {`Credit: ${state.symbol}${formatNumber(state.creditCount)}`}
                </span>
            )} */}
            {state.groupedByCount > 10 && !props.showAllRecords && (
                <ChartLimitNote>The top 10 {GetGroupByPlurals(props.groupBy)} are shown</ChartLimitNote>
            )}
        </div>
    );
};

export interface DebtOwingParams {
    groupBy: string | null;
    showAllRecords?: boolean;
    entityType: number | null;
    entityGuid: string | null;
    responsibility: number | null;
    include: boolean | null;
}

export interface DebtOwingData {
    statistics: Statistics;
}

export interface Statistics {
    matter: Matter;
}

export interface Matter {
    debtsOwing: DebtsOwing;
}

export interface DebtsOwing {
    total: number;
    groupedByCount: number;
    debtOwing: DebtOwing[];
    creditCount: number;
}

export interface DebtOwing {
    amount: number;
    groupedBy: string;
    matters: number;
}

const DEBT_OWING_QUERY = gql`
    query DebtOwingData($groupBy: MatterGroupBy, $entityType: Int, $entityGuid: String, $responsibility: Int, $showAllRecords: Boolean) {
        statistics {
            matter {
                debtsOwing(groupBy: $groupBy, entityType: $entityType, entityGuid: $entityGuid, responsibility: $responsibility, showAllRecords: $showAllRecords) {
                    total
                    groupedByCount
                    creditCount
                    debtOwing {
                        amount
                        groupedBy
                        matters
                    }
                }
            }
        }
    }
`;

export interface CurrencySymbolData {
    options: Options;
}

export interface Options {
    currencySymbol: CurrencySymbol;
}

export interface CurrencySymbol {
    symbol: string;
}

const CURRENCY_SYMBOL_QUERY = gql`
    query CurrencySymbol {
        options {
            currencySymbol {
                symbol
            }
        }
    }
`;
